import type { SearchResponse } from '@algolia/client-search'
import type { AlgoliaEventItem } from '~/composables/types/algolia'

export const KITS_BROWSE_PER_PAGE = 20

const priceAmountCents = (euros: number) => Math.round(euros * 100)

export const useKitsBrowseProducts = () => {
  const searchClient = useAlgoliaRef()
  const { locale } = useI18n()

  const page = ref(1)
  const categorySlug = ref<string | null>(null)
  const saleOnly = ref(false)
  const priceMinEur = ref<number | undefined>()
  const priceMaxEur = ref<number | undefined>()

  const { data, pending, error, refresh } = useAsyncData(
    'kits-browse-algolia',
    async () => {
      const filterParts: string[] = []
      if (saleOnly.value) {
        filterParts.push('(has_promotions:true)')
      }
      if (priceMinEur.value != null) {
        filterParts.push(
          `(default_price.amount >= ${priceAmountCents(priceMinEur.value)})`
        )
      }
      if (priceMaxEur.value != null) {
        filterParts.push(
          `(default_price.amount <= ${priceAmountCents(priceMaxEur.value)})`
        )
      }

      const query = {
        indexName: getBoxesIndexName(locale.value),
        hitsPerPage: KITS_BROWSE_PER_PAGE,
        page: page.value - 1,
        clickAnalytics: true,
        ...(categorySlug.value
          ? {
              facetFilters: [
                `categories.name:${categorySlug.value}`,
              ] as string[],
            }
          : {}),
        ...(filterParts.length > 0
          ? { filters: filterParts.join(' AND ') }
          : {}),
      }

      return searchClient.search<AlgoliaEventItem>([query])
    },
    {
      watch: [page, categorySlug, saleOnly, priceMinEur, priceMaxEur, locale],
    }
  )

  watch([categorySlug, saleOnly, priceMinEur, priceMaxEur, locale], () => {
    page.value = 1
  })

  const firstResult = computed(
    () =>
      data.value?.results?.[0] as SearchResponse<AlgoliaEventItem> | undefined
  )

  const products = computed(() => firstResult.value?.hits ?? [])

  const totalHits = computed(() => firstResult.value?.nbHits ?? 0)

  const totalPages = computed(() => {
    const nb = firstResult.value?.nbPages
    if (nb == null || nb < 1) return 1
    return nb
  })

  return {
    products,
    totalHits,
    totalPages,
    pending,
    error,
    refresh,
    page,
    perPage: KITS_BROWSE_PER_PAGE,
    categorySlug,
    saleOnly,
    priceMinEur,
    priceMaxEur,
  }
}
